import { useState, useEffect } from "react";

import { css } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { extendBfldrCacheTime } from "@/components/wpl/image/src/image";
import { Picture } from "@/components/wpl/image/src/image";
import { wacToken } from "@/libraries/design/foundations";

type OptionsTypes = {
    title: string;
    image: string;
    description: string;
};

type JswSectionCollaborateTypes = {
    heading: string;
    options: OptionsTypes[];
};

/**
 * @deprecated - Please use @/libraries/design/patterns/features-highlight instead
 */
export const JswCollaborate = ({
    heading,
    options
}: JswSectionCollaborateTypes) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();
    const [activeOption, setActiveOption] = useState(0);
    const { formatMessage } = useIntl();

    const changeActive = (index: number) => {
        // Cancel all animations before activeOption change
        const progressGrowAnimations = document
            .getAnimations()
            .filter((animation) => {
                return (
                    animation instanceof CSSAnimation &&
                    animation.animationName === "progressgrow"
                );
            });
        progressGrowAnimations.forEach((animation) => {
            animation.cancel();
        });

        setActiveOption(index);
    };

    useEffect(() => {
        const progressGrowAnimations = document
            .getAnimations()
            .filter((animation) => {
                return (
                    animation instanceof CSSAnimation &&
                    animation.animationName === "progressgrow"
                );
            });

        // set finish event
        progressGrowAnimations.forEach((animation) => {
            animation.onfinish = () => {
                setActiveOption(
                    activeOption == options.length - 1 ? 0 : activeOption + 1
                );
                animation.cancel();
            };
        });

        // Only cycle when visible
        if (!sectionIsVisible) {
            progressGrowAnimations.forEach((animation) => {
                animation.pause();
            });
        } else {
            progressGrowAnimations.forEach((animation) => {
                animation.play();
            });
        }
    }, [activeOption, options.length, sectionIsVisible]);

    return (
        <div
            css={empowerStyles}
            ref={scrollRef}
            className={sectionIsVisible ? "is-visible" : ""}
        >
            <div className="top-section" css={topSectionForPictureStyles}>
                <h2 css={h2Styles}>
                    {heading}
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/s31k3rin80p38d25a3wn54b8354r27vm.webp",
                            alt: "",
                            width: 245,
                            height: 279
                        }}
                    />
                </h2>
            </div>
            <div className="bottom-section" css={bottomSectionStyles}>
                <div
                    aria-label={formatMessage({
                        id: "jira-product-tour.collaborate-section.carousel",
                        defaultMessage: "Carousel"
                    })}
                    className="row-width"
                    css={rowWidthStyles}
                    role="region"
                >
                    <div className="half texts" css={halfStyles}>
                        {options.map((option, index) => {
                            const src = extendBfldrCacheTime(option.image);
                            const isActive = activeOption == index;
                            return (
                                <div className="option-container" key={index}>
                                    <button
                                        className={`option ${
                                            activeOption == index
                                                ? "active"
                                                : "inactive"
                                        }`}
                                        css={buttonStyles}
                                        onClick={() => changeActive(index)}
                                    >
                                        <div
                                            className="progress"
                                            css={progressStyles}
                                        >
                                            <div css={innerDivProgressStyles} />
                                        </div>
                                        <h3 css={optionH3Styles}>
                                            {option.title}
                                        </h3>
                                        <p css={optionDescriptionStyles}>
                                            {option.description}
                                        </p>
                                    </button>
                                    <div
                                        className="mobile-images"
                                        css={mobileImagesStyles}
                                    >
                                        <Picture
                                            image={{
                                                src,
                                                alt: option.title
                                            }}
                                            style={{
                                                aspectRatio: "5/4",
                                                display: isActive
                                                    ? "block"
                                                    : "none",
                                                width: "100%",
                                                height: "auto"
                                            }}
                                            css={optionContainerImageStyles}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div
                        aria-live="polite"
                        className="half images"
                        css={halfStyles}
                        role="status"
                    >
                        {options.map((option, index) => {
                            const src = extendBfldrCacheTime(option.image);
                            const isActive = activeOption == index;
                            return (
                                <div
                                    key={index}
                                    className="desktop-images"
                                    css={desktopImagesStyles}
                                    hidden={!isActive}
                                >
                                    <Picture
                                        image={{
                                            src,
                                            alt: option.title
                                        }}
                                        style={{
                                            aspectRatio: "5/4",
                                            position: "absolute",
                                            width: "100%",
                                            height: "auto"
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

const topSectionForPictureStyles = css({
    textAlign: "center",
    [media.below.lg]: {
        marginInline: "10%"
    },
    [media.below.md]: {
        maxWidth: "580px"
    },
    // disable needed to apply styling to <Picture /> component
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "57px",
        height: "66px",
        transform: "rotate(3.234deg)",
        [media.only.md]: {
            width: "37px",
            height: "46px"
        },
        [media.only.sm]: {
            width: "27px",
            height: "36px",
            paddingBlockStart: "5px"
        },
        [media.below.sm]: {
            width: "27px",
            height: "36px",
            paddingBlockStart: "5px"
        }
    }
});

const mobileImagesStyles = css({
    display: "none",
    [media.below.lg]: {
        display: "grid",
        justifyItems: "center",
        alignItems: "center"
    }
});

const desktopImagesStyles = css({
    [media.below.lg]: {
        display: "none"
    }
});

const rowWidthStyles = css({
    maxWidth: "1187px",
    display: "flex",
    flexWrap: "wrap",
    gap: "32px",
    [media.below.lg]: {
        width: "100%",
        flexDirection: "column",
        alignItems: "center",
        justifyItems: "center",
        gap: "0px"
    }
});

const halfStyles = css({
    flexBasis: "calc(50% - 16px)",
    position: "relative",
    ["&.images"]: {
        aspectRatio: "5/4"
    }
});

const progressStyles = css({
    position: "absolute",
    insetInlineStart: 0,
    insetBlockStart: 0,
    insetBlockEnd: 0,
    width: "3px",
    height: "100%",
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#C1C7D0"
});

const buttonStyles = css({
    background: "none",
    appearance: "none",
    inset: 0,
    cursor: "pointer",
    border: 0,
    fontSize: "1rem",
    textAlign: "left",
    margin: "0 0 32px",
    padding: "0 0 0 20px",
    position: "relative",
    height: "100%",
    ["&.active"]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        h3: {
            fontSize: "32px",
            lineHeight: "38px",
            [media.below.lg]: {
                fontSize: "24px",
                lineHeight: "30px"
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        p: {
            maxHeight: "150px"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [".progress"]: {
            height: "auto",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            div: {
                animation: "progressgrow linear 5s 1"
            }
        }
    }
});

const optionDescriptionStyles = css({
    transition: "max-height 0.5s",
    maxHeight: 0,
    margin: 0,
    overflow: "hidden"
});

const innerDivProgressStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#0065FF",
    height: "0%"
});

const optionH3Styles = css({
    fontSize: "24px",
    lineHeight: "30px",
    fontWeight: "700",
    margin: 0,
    transition: "font-size 0.5s",
    [media.below.lg]: {
        fontWeight: "600"
    }
});

const bottomSectionStyles = css({
    [media.below.lg]: {
        marginInline: "10%",
        paddingBlockStart: "32px"
    },

    paddingBlockStart: "50px",
    background: wacToken("color.neutral.light0")
});

const optionContainerImageStyles = css({
    position: "relative",
    maxWidth: "600px"
});

const h2Styles = css({
    fontWeight: "700",
    fontSize: "48px",
    lineHeight: "56px",
    margin: "0 0 30px",
    [media.below.lg]: {
        fontSize: "30px",
        lineHeight: "36px",
        margin: "0"
    }
});

const empowerStyles = css({
    ["@keyframes progressgrow"]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["0%"]: {
            height: "0%"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["100%"]: {
            height: "100%"
        }
    },
    position: "relative",
    display: "grid",
    justifyItems: "center",
    alignItems: "center",
    paddingBlock: "50px",
    marginInline: "32px",
    [media.below.lg]: {
        paddingBlock: "32px",
        marginInline: "0px"
    }
});
